import { supabase } from '../config/supabase'
import { batchGetProfessionalRatings } from './reviewService'
import { applySalonFilter, getSelectedSalonId } from '../utils/salonScope'

const LIST_CACHE_TTL_MS = 5 * 60 * 1000
let listCache = null
let listInflight = null

export function clearProfessionalsCache() {
  listCache = null
  listInflight = null
}

/**
 * Fetch all active professionals with their specializations
 * PERFORMANCE OPTIMIZED: Uses batch rating calculation instead of N+1 queries
 * Results are cached per salon for a short window so repeat page visits are instant.
 */
export const getProfessionals = async () => {
  const salonKey = String(getSelectedSalonId() || 'all')

  if (listCache && listCache.key === salonKey && Date.now() - listCache.at <= LIST_CACHE_TTL_MS) {
    return listCache.value
  }
  if (listInflight && listInflight.key === salonKey) {
    return listInflight.promise
  }

  const promise = fetchProfessionals()
    .then((result) => {
      if (!result.error) listCache = { key: salonKey, at: Date.now(), value: result }
      return result
    })
    .finally(() => {
      if (listInflight && listInflight.promise === promise) listInflight = null
    })

  listInflight = { key: salonKey, promise }
  return promise
}

const fetchProfessionals = async () => {
  try {
    let query = supabase
      .from('professionals')
      .select(`
        id,
        name,
        role,
        rating,
        image_url,
        bio,
        is_active,
        created_at,
        updated_at,
        salon_id,
        professional_specializations (
          category_id,
          excluded_service_ids,
          service_categories (
            id,
            name
          )
        )
      `)
      .eq('is_active', true)
    query = applySalonFilter(query)
    const { data, error } = await query.order('name', { ascending: true })

    if (error) throw error
    
    // PERFORMANCE OPTIMIZATION: Batch calculate all ratings in a single query instead of N+1
    if (data && data.length > 0) {
      console.log('📊 Batch calculating ratings from verified reviews for', data.length, 'professionals...')
      
      // Extract all professional IDs
      const professionalIds = data.map(p => p.id).filter(Boolean)
      
      // Batch calculate all ratings in one query
      const ratingsMap = await batchGetProfessionalRatings(professionalIds)
            
      // Map ratings to professionals from verified reviews only.
      // New / unreviewed professionals must show 0 (not the DB column default of 5.0).
      const professionalsWithRatings = data.map(professional => {
        const ratingData = ratingsMap[professional.id] || { average: 0, count: 0 }
        return {
          ...professional,
          rating: ratingData.count > 0 ? ratingData.average : 0,
          reviewCount: ratingData.count
        }
      })
      
      console.log('✅ Professionals with batch-calculated ratings:', professionalsWithRatings.length)
      
      return { data: professionalsWithRatings, error: null }
    }
    
    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error fetching professionals:', error)
    return { data: null, error }
  }
}

/**
 * Fetch a single professional by ID
 */
export const getProfessionalById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('professionals')
      .select(`
        *,
        professional_specializations (
          category_id,
          excluded_service_ids,
          service_categories (
            id,
            name
          )
        )
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching professional:', error)
    return { data: null, error }
  }
}

/**
 * Fetch professionals by specialization (category)
 */
export const getProfessionalsByCategory = async (categoryId) => {
  try {
    const { data, error } = await supabase
      .from('professionals')
      .select(`
        *,
        professional_specializations!inner (
          category_id,
          service_categories (
            id,
            name
          )
        )
      `)
      .eq('professional_specializations.category_id', categoryId)
      .eq('is_active', true)
      .order('name', { ascending: true })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching professionals by category:', error)
    return { data: null, error }
  }
}

/**
 * Create a new professional
 */
export const createProfessional = async (professionalData) => {
  try {
    const { getSelectedSalonId } = await import('../utils/salonScope')
    const payload = {
      ...professionalData,
      salon_id: professionalData.salon_id || getSelectedSalonId() || null,
    }
    const { data, error } = await supabase
      .from('professionals')
      .insert([payload])
      .select()
      .single()

    if (error) throw error
    clearProfessionalsCache()
    return { data, error: null }
  } catch (error) {
    console.error('Error creating professional:', error)
    return { data: null, error }
  }
}

/**
 * Update a professional
 */
export const updateProfessional = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('professionals')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    clearProfessionalsCache()
    return { data, error: null }
  } catch (error) {
    console.error('Error updating professional:', error)
    return { data: null, error }
  }
}

/**
 * Delete a professional
 */
export const deleteProfessional = async (id) => {
  try {
    const { error } = await supabase
      .from('professionals')
      .delete()
      .eq('id', id)

    if (error) throw error
    clearProfessionalsCache()
    return { error: null }
  } catch (error) {
    console.error('Error deleting professional:', error)
    return { error }
  }
}

/**
 * Deactivate a professional (soft delete)
 */
export const deactivateProfessional = async (id) => {
  try {
    const { data, error } = await supabase
      .from('professionals')
      .update({ 
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    clearProfessionalsCache()
    return { data, error: null }
  } catch (error) {
    console.error('Error deactivating professional:', error)
    return { data: null, error }
  }
}

/**
 * Add specialization to professional
 */
export const addProfessionalSpecialization = async (professionalId, categoryId) => {
  try {
    const { data, error } = await supabase
      .from('professional_specializations')
      .insert([{
        professional_id: professionalId,
        category_id: categoryId
      }])
      .select()
      .single()

    if (error) throw error
    clearProfessionalsCache()
    return { data, error: null }
  } catch (error) {
    console.error('Error adding specialization:', error)
    return { data: null, error }
  }
}

/**
 * Remove specialization from professional
 */
export const removeProfessionalSpecialization = async (professionalId, categoryId) => {
  try {
    const { error } = await supabase
      .from('professional_specializations')
      .delete()
      .eq('professional_id', professionalId)
      .eq('category_id', categoryId)

    if (error) throw error
    clearProfessionalsCache()
    return { error: null }
  } catch (error) {
    console.error('Error removing specialization:', error)
    return { error }
  }
}

/**
 * Search professionals by name
 */
export const searchProfessionals = async (searchTerm) => {
  try {
    const { data, error } = await supabase
      .from('professionals')
      .select(`
        *,
        professional_specializations (
          category_id,
          excluded_service_ids,
          service_categories (
            id,
            name
          )
        )
      `)
      .eq('is_active', true)
      .ilike('name', `%${searchTerm}%`)
      .order('name', { ascending: true })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error searching professionals:', error)
    return { data: null, error }
  }
}

/**
 * Normalize appointment/catalog service fields (bookings often use service_id / service_name).
 */
export const normalizeServiceIdentity = (service) => {
  if (!service || typeof service !== 'object') {
    return { serviceId: null, serviceName: null }
  }
  return {
    serviceId: service.id || service.service_id || null,
    serviceName: service.name || service.service_name || service.title || null,
  }
}

/**
 * Resolve the current-salon catalog category for a service.
 * Prefers local catalog match by id/name so cross-salon service UUIDs in old
 * appointments still map to this salon's Featured/Nails/etc. category.
 */
export const resolveCatalogCategoryForService = (categories, service) => {
  const list = Array.isArray(categories) ? categories : []
  const { serviceId, serviceName } = normalizeServiceIdentity(service)
  const nameLower = serviceName ? String(serviceName).toLowerCase().trim() : ''

  let cat = list.find((c) =>
    c?.services?.some((s) => serviceId && String(s.id) === String(serviceId))
  )
  if (cat) return cat

  if (nameLower) {
    cat = list.find((c) =>
      c?.services?.some(
        (s) => String(s.name || '').toLowerCase().trim() === nameLower
      )
    )
    if (cat) return cat
  }

  if (service?.category_id) {
    cat = list.find((c) => String(c.id) === String(service.category_id))
    if (cat) return cat
  }

  return null
}

/**
 * Resolve salon-local category id/name + service id for professional matching.
 */
export const resolveServiceMatchingContext = (categories, service) => {
  const { serviceId, serviceName } = normalizeServiceIdentity(service)
  const catalogCategory = resolveCatalogCategoryForService(categories, service)
  const nameLower = serviceName ? String(serviceName).toLowerCase().trim() : ''
  const catalogService =
    catalogCategory?.services?.find(
      (s) =>
        (serviceId && String(s.id) === String(serviceId)) ||
        (nameLower && String(s.name || '').toLowerCase().trim() === nameLower)
    ) || null

  return {
    serviceId: catalogService?.id || serviceId,
    serviceName: catalogService?.name || serviceName,
    categoryId:
      catalogCategory?.id ||
      service?.category_id ||
      service?.category?.id ||
      null,
    categoryName:
      catalogCategory?.name ||
      service?.categoryName ||
      service?.category_name ||
      service?.group ||
      null,
    catalogCategory,
    catalogService,
  }
}

/**
 * Resolve a professional specialization that matches a service category.
 * Matches by category_id first, then by category name (brand vs salon Featured IDs).
 */
export const findMatchingSpecialization = (professional, categoryId, categoryName) => {
  const specs = professional?.professional_specializations
  if (!Array.isArray(specs) || specs.length === 0) return null

  if (categoryId != null && categoryId !== '') {
    const byId = specs.find((spec) => String(spec.category_id) === String(categoryId))
    if (byId) return byId
  }

  const targetName = categoryName ? String(categoryName).toLowerCase().trim() : ''
  if (!targetName) return null

  return (
    specs.find((spec) => {
      const name = spec?.service_categories?.name
      return name && String(name).toLowerCase().trim() === targetName
    }) || null
  )
}

/**
 * Check if a service is excluded for a professional in a specific category
 * @param {Object} professional - Professional object with specializations
 * @param {string} categoryId - Category ID
 * @param {string} serviceId - Service ID to check
 * @param {string} [categoryName] - Optional category name fallback (e.g. Featured)
 * @returns {boolean} - True if service is excluded, false otherwise
 */
export const isServiceExcludedForProfessional = (professional, categoryId, serviceId, categoryName) => {
  if (!professional || !serviceId) return false

  const specialization = findMatchingSpecialization(professional, categoryId, categoryName)
  if (!specialization || !specialization.excluded_service_ids) {
    return false
  }

  const excludedIds = Array.isArray(specialization.excluded_service_ids)
    ? specialization.excluded_service_ids
    : []

  return excludedIds.some((id) => String(id) === String(serviceId))
}

/**
 * "Any professional" is only valid when at least one real specialist can do the service.
 */
export const withAnyProfessionalOption = (eligible, anyOption) => {
  if (!Array.isArray(eligible) || eligible.length === 0) return []
  if (!anyOption) return eligible
  if (eligible.some((member) => member?.id === 'any')) return eligible
  return [anyOption, ...eligible]
}

/**
 * Filter professionals who can perform a specific service
 * @param {Array} professionals - Array of professional objects
 * @param {string} categoryId - Category ID
 * @param {string} serviceId - Service ID
 * @param {string} [categoryName] - Optional category name fallback (e.g. Featured)
 * @returns {Array} - Filtered array of professionals who can perform the service
 */
export const filterProfessionalsForService = (professionals, categoryId, serviceId, categoryName) => {
  if (!professionals || !Array.isArray(professionals)) {
    return []
  }

  return professionals.filter((professional) => {
    const matchingSpec = findMatchingSpecialization(professional, categoryId, categoryName)
    if (!matchingSpec) return false
    return !isServiceExcludedForProfessional(professional, categoryId, serviceId, categoryName)
  })
}

